import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { getDoc } from "firebase/firestore";
import moment, { Moment } from "moment-timezone";
import { useUsuario } from "../../../providers/usuarioProvider";
import { useSnackBar } from "../../../stores/snackBarStores";
import Functions from "../../../callable/functions";
import PosicaoInternacionalDb from "../../../db/posicaoInternacionalDb";
import UsuarioDb from "../../../db/usuarioDb";
import PosicaoAtivoInternacional from "../../../models/posicaoAtivoInternacional";
import InteiroInput from "../../util/ui/inputs/inteiroInput";
import MoedaText from "../../util/ui/inputs/moedaText";
import {
  MoedaInternacional,
  ROTULO_EVENTO_INTERNACIONAL,
  TipoEventoInternacional,
  TipoOperacaoInternacional,
} from "../../../models/enum";
import { escalaQuantidade } from "../../util/ui/inputs/quantidade";

type Props = {
  open: boolean;
  onClose: () => void;
  // Mês em foco: vira a data inicial sugerida (último dia do mês ou hoje).
  mes?: Moment;
  onRegistrado?: () => void;
};

const TZ = "America/Sao_Paulo";
const DATA_MINIMA = moment.tz("2024-01-01", TZ).startOf("day");

/**
 * Registro manual de evento corporativo internacional (desdobramento,
 * grupamento ou troca de ticker). Enquanto os leitores de statement não
 * extraem esses eventos, é por aqui que o contador informa "NFLX 1 para 10 em
 * 17/11/2025" ou "FI virou FISV em 11/11/2025". O evento vira uma linha nos
 * Lançamentos e o mês é recalculado.
 */
export default function RegistrarEventoInternacionalDialog({
  open,
  onClose,
  mes,
  onRegistrado,
}: Props) {
  const { visualizado } = useUsuario();
  const dispatchSnack = useSnackBar();

  const hoje = moment.tz(TZ).startOf("day");
  const dataInicial = mes
    ? moment.min(mes.clone().endOf("month").startOf("day"), hoje)
    : hoje;

  const [ativo, setAtivo] = useState("");
  const [data, setData] = useState<Moment | null>(dataInicial);
  const [evento, setEvento] = useState<TipoEventoInternacional>(
    TipoEventoInternacional.DESDOBRAMENTO,
  );
  const [moeda, setMoeda] = useState<MoedaInternacional>(
    MoedaInternacional.USD,
  );
  const [de, setDe] = useState<number>(1);
  const [para, setPara] = useState<number>(2);
  const [codigoNovo, setCodigoNovo] = useState("");
  const [salvando, setSalvando] = useState(false);
  // Custódia do dia anterior ao evento: sugestões de ativo e prévia do ajuste.
  const [custodia, setCustodia] = useState<PosicaoAtivoInternacional[]>([]);
  // Piso de data do usuário visualizado: o backend recusa evento antes do
  // início internacional dele, então o calendário já não oferece essas datas.
  const [dataMinima, setDataMinima] = useState<Moment>(DATA_MINIMA);

  useEffect(() => {
    if (!visualizado) return;
    let cancelado = false;
    getDoc(new UsuarioDb().doc({ usuarioId: visualizado }))
      .then((doc) => {
        if (cancelado) return;
        const inicio = doc.data()?.dataInicioInternacional;
        setDataMinima(
          inicio
            ? moment.max(DATA_MINIMA, inicio.clone().tz(TZ).startOf("day"))
            : DATA_MINIMA,
        );
      })
      .catch(() => {
        if (!cancelado) setDataMinima(DATA_MINIMA);
      });
    return () => {
      cancelado = true;
    };
  }, [visualizado]);

  useEffect(() => {
    if (!visualizado || !data?.isValid()) {
      setCustodia([]);
      return;
    }
    // Flag de cancelamento: trocar a data (ou o cliente) rápido não pode
    // deixar uma resposta antiga sobrescrever a atual — a moeda e a prévia
    // seguem a custódia carregada, e ela precisa ser a da data escolhida.
    let cancelado = false;
    const vespera = data.clone().subtract(1, "day").format("YYYY-MM-DD");
    getDoc(
      new PosicaoInternacionalDb().doc({
        usuarioId: visualizado,
        posicaoId: vespera,
      }),
    )
      .then((doc) => {
        if (cancelado) return;
        setCustodia(
          doc.exists()
            ? doc.data()!.posicoes.filter((p) => p.quantidade > 0)
            : [],
        );
      })
      .catch(() => {
        if (!cancelado) setCustodia([]);
      });
    return () => {
      cancelado = true;
    };
  }, [visualizado, data?.format("YYYY-MM-DD")]);

  const posicao = useMemo(
    () => custodia.find((p) => p.codigoAtivo === ativo.trim().toUpperCase()),
    [custodia, ativo],
  );

  // Moeda acompanha a posição encontrada
  useEffect(() => {
    if (posicao) setMoeda(posicao.moeda);
  }, [posicao]);

  const trocaDeTicker = evento === TipoEventoInternacional.ALTERACAO_CODIGO;
  const fator = trocaDeTicker ? 1 : de > 0 && para > 0 ? para / de : 0;
  const desdobramento = evento === TipoEventoInternacional.DESDOBRAMENTO;
  const proporcaoCoerente =
    fator > 0 && de !== para && (desdobramento ? para > de : para < de);
  const codigoNovoLimpo = codigoNovo.trim().toUpperCase();
  const codigoNovoValido =
    codigoNovoLimpo.length > 0 &&
    codigoNovoLimpo !== ativo.trim().toUpperCase();
  // Posição já existente no código novo: o cálculo funde as duas.
  const posicaoNova = useMemo(
    () =>
      trocaDeTicker
        ? custodia.find((p) => p.codigoAtivo === codigoNovoLimpo)
        : undefined,
    [custodia, codigoNovoLimpo, trocaDeTicker],
  );
  // O cálculo funde só posições na mesma direção e moeda; fora disso ele
  // pararia com erro, então o diálogo já recusa.
  const fusaoIncompativel =
    !!posicao &&
    !!posicaoNova &&
    ((posicaoNova.tipoOperacao ?? TipoOperacaoInternacional.BUY) !==
      (posicao.tipoOperacao ?? TipoOperacaoInternacional.BUY) ||
      posicaoNova.moeda !== posicao.moeda);
  const detalhesValidos = trocaDeTicker
    ? codigoNovoValido && !fusaoIncompativel
    : proporcaoCoerente;
  const valido =
    ativo.trim().length > 0 &&
    !!data?.isValid() &&
    !data.isAfter(hoje, "day") &&
    !data.isBefore(dataMinima, "day") &&
    detalhesValidos;

  const registrar = async () => {
    if (!valido || !data) return;
    setSalvando(true);
    try {
      await new Functions().registrarEventoInternacional({
        ativo: ativo.trim().toUpperCase(),
        data: data.format("YYYY-MM-DD"),
        evento,
        moeda,
        ...(trocaDeTicker ? { codigoNovo: codigoNovoLimpo } : { de, para }),
      });
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem: `${ROTULO_EVENTO_INTERNACIONAL[evento]} (${ativo
            .trim()
            .toUpperCase()}) registrado. Recalcule o mês para aplicar.`,
          severidade: "success",
        },
      });
      onRegistrado?.();
      onClose();
    } catch (error: any) {
      console.error(error);
      dispatchSnack({
        type: "ALERT",
        payload: {
          mensagem:
            error?.message ??
            "Não foi possível registrar o evento. Tente novamente.",
          severidade: "error",
        },
      });
    } finally {
      setSalvando(false);
    }
  };

  const quantidadeNova = posicao ? posicao.quantidade * fator : undefined;
  const custoMedioNovo =
    posicao && fator > 0 ? posicao.custoMedioOriginal / fator : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle color="text.primary" sx={{ typography: "h3" }}>
        Registrar evento corporativo
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {trocaDeTicker
            ? "A posição passa a responder pelo novo código a partir da data, com a mesma quantidade e o mesmo custo. Não gera resultado. O mês será recalculado."
            : "Ajusta quantidade e custo médio da posição na data do evento, mantendo o custo total. Não gera resultado. O mês será recalculado."}
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
        >
          <Autocomplete
            freeSolo
            options={custodia.map((p) => p.codigoAtivo)}
            inputValue={ativo}
            onInputChange={(_e, value) => setAtivo(value.toUpperCase())}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Ativo"
                placeholder="NFLX"
                inputProps={{
                  ...params.inputProps,
                  style: { textTransform: "uppercase" },
                }}
              />
            )}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Data do evento"
              slotProps={{ textField: { size: "small" } }}
              value={data}
              minDate={dataMinima}
              maxDate={hoje}
              onChange={(e) => {
                if (e?.isValid()) setData(e.clone().startOf("day"));
              }}
            />
          </LocalizationProvider>
          <TextField
            select
            size="small"
            label="Tipo de evento"
            value={evento}
            onChange={(e) =>
              setEvento(e.target.value as TipoEventoInternacional)
            }
          >
            {Object.values(TipoEventoInternacional).map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {ROTULO_EVENTO_INTERNACIONAL[tipo]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Moeda da posição"
            value={moeda}
            onChange={(e) => setMoeda(e.target.value as MoedaInternacional)}
          >
            {Object.values(MoedaInternacional).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          {trocaDeTicker ? (
            <Box gridColumn="1 / -1">
              <TextField
                size="small"
                label="Novo código"
                placeholder="FISV"
                value={codigoNovo}
                onChange={(e) => setCodigoNovo(e.target.value.toUpperCase())}
                inputProps={{ style: { textTransform: "uppercase" } }}
                error={codigoNovo.trim().length > 0 && !codigoNovoValido}
                helperText={
                  codigoNovo.trim().length > 0 && !codigoNovoValido
                    ? "O novo código precisa ser diferente do atual."
                    : " "
                }
                sx={{ width: { xs: "100%", sm: 220 } }}
              />
            </Box>
          ) : (
            <Box gridColumn="1 / -1">
              <Typography
                variant="labelMono"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Proporção
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="body2" color="text.secondary">
                  de
                </Typography>
                <TextField
                  size="small"
                  value={de}
                  onChange={(e) => setDe((e.target as any).floatValue ?? 0)}
                  InputProps={{
                    // Mesmo cast usado no ponto de partida: a tipagem do NumericFormat não casa
                    // com InputBaseComponentProps.
                    inputComponent: InteiroInput as any,
                    inputProps: { decimalScale: 4 },
                  }}
                  sx={{ width: 110 }}
                />
                <Typography variant="body2" color="text.secondary">
                  para
                </Typography>
                <TextField
                  size="small"
                  value={para}
                  onChange={(e) => setPara((e.target as any).floatValue ?? 0)}
                  InputProps={{
                    // Mesmo cast usado no ponto de partida: a tipagem do NumericFormat não casa
                    // com InputBaseComponentProps.
                    inputComponent: InteiroInput as any,
                    inputProps: { decimalScale: 4 },
                  }}
                  sx={{ width: 110 }}
                />
              </Box>
              {!proporcaoCoerente && de > 0 && para > 0 && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {desdobramento
                    ? "Num desdobramento a quantidade final é maior que a inicial (ex.: 1 para 10)."
                    : "Num grupamento a quantidade final é menor que a inicial (ex.: 5 para 1)."}
                </Typography>
              )}
            </Box>
          )}
          <Box gridColumn="1 / -1">
            {trocaDeTicker && codigoNovoValido && fusaoIncompativel ? (
              <Alert severity="error" icon={false}>
                <Typography variant="body2">
                  Já existe posição em {codigoNovoLimpo} em direção
                  (comprada/vendida) ou moeda diferente da posição de{" "}
                  {ativo.trim().toUpperCase()}. As duas não podem ser fundidas;
                  confira os lançamentos antes de registrar a troca.
                </Typography>
              </Alert>
            ) : trocaDeTicker && codigoNovoValido && ativo.trim() ? (
              <Alert severity={posicao ? "info" : "warning"} icon={false}>
                <Typography variant="body2">
                  <Typography
                    component="span"
                    variant="dataMono"
                    fontSize="inherit"
                  >
                    {ativo.trim().toUpperCase()}
                  </Typography>{" "}
                  passa a se chamar{" "}
                  <Typography
                    component="span"
                    variant="dataMono"
                    fontSize="inherit"
                  >
                    {codigoNovoLimpo}
                  </Typography>
                  {data ? ` a partir de ${data.format("DD/MM/YYYY")}` : ""}.{" "}
                  {posicao ? (
                    <>
                      Posição de{" "}
                      <Typography
                        component="span"
                        variant="dataMono"
                        fontSize="inherit"
                      >
                        {posicao.quantidade.toLocaleString("pt-BR", {
                          maximumFractionDigits: escalaQuantidade(
                            posicao.quantidade,
                          ),
                        })}
                      </Typography>{" "}
                      cotas a{" "}
                      <MoedaText
                        value={posicao.custoMedioOriginal}
                        prefix={`${posicao.moeda} `}
                      />{" "}
                      segue igual, só com o código novo.
                      {posicaoNova ? (
                        <>
                          {" "}
                          Já existe posição em {codigoNovoLimpo} (
                          {posicaoNova.quantidade.toLocaleString("pt-BR", {
                            maximumFractionDigits: escalaQuantidade(
                              posicaoNova.quantidade,
                            ),
                          })}{" "}
                          cotas): as duas serão fundidas pelo custo médio.
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      Sem posição nesse ativo na véspera do evento. Dá para
                      registrar mesmo assim; o cálculo aplica a troca se houver
                      posição na data.
                    </>
                  )}
                </Typography>
              </Alert>
            ) : !trocaDeTicker && proporcaoCoerente && ativo.trim() ? (
              <Alert severity={posicao ? "info" : "warning"} icon={false}>
                <Typography variant="body2">
                  Cada{" "}
                  <Typography
                    component="span"
                    variant="dataMono"
                    fontSize="inherit"
                  >
                    {de}
                  </Typography>{" "}
                  cota de{" "}
                  <Typography
                    component="span"
                    variant="dataMono"
                    fontSize="inherit"
                  >
                    {ativo.trim().toUpperCase()}
                  </Typography>{" "}
                  vira{" "}
                  <Typography
                    component="span"
                    variant="dataMono"
                    fontSize="inherit"
                  >
                    {para}
                  </Typography>
                  .{" "}
                  {posicao && data && quantidadeNova !== undefined ? (
                    <>
                      Posição em {data.format("DD/MM/YYYY")}:{" "}
                      <Typography
                        component="span"
                        variant="dataMono"
                        fontSize="inherit"
                      >
                        {posicao.quantidade.toLocaleString("pt-BR", {
                          maximumFractionDigits: escalaQuantidade(
                            posicao.quantidade,
                          ),
                        })}
                      </Typography>{" "}
                      →{" "}
                      <Typography
                        component="span"
                        variant="dataMono"
                        fontSize="inherit"
                      >
                        {quantidadeNova.toLocaleString("pt-BR", {
                          maximumFractionDigits:
                            escalaQuantidade(quantidadeNova),
                        })}
                      </Typography>{" "}
                      cotas, custo médio{" "}
                      <MoedaText
                        value={posicao.custoMedioOriginal}
                        prefix={`${posicao.moeda} `}
                      />{" "}
                      →{" "}
                      <MoedaText
                        value={custoMedioNovo}
                        prefix={`${posicao.moeda} `}
                      />
                      .
                    </>
                  ) : (
                    <>
                      Sem posição nesse ativo na véspera do evento. Dá para
                      registrar mesmo assim; o cálculo aplica o ajuste se houver
                      posição na data.
                    </>
                  )}
                </Typography>
              </Alert>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={salvando}
          disabled={!valido}
          onClick={registrar}
        >
          Registrar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
